/**
 * Whether the "Translate now" control is offered during a meeting.
 *
 * On for a browser-captured meeting: the button and its Space shortcut both
 * show while recording or paused. A native meeting still hides them, because
 * its sockets have no such command. Flip this to `false` to hide both again —
 * `MeetingChat.translationHard.test.tsx` covers both states.
 *
 * Annotated `boolean` rather than left to infer `true`, so neither side of the
 * flag reads as dead code.
 */
export const TRANSLATION_HARD_ENABLED: boolean = true

/**
 * Whether the "Upcoming" section is shown on Home.
 *
 * On: BRAI-458 supplies Microsoft Calendar events instead of treating abandoned
 * BrSE recordings as scheduled meetings (BRAI-446).
 */
export const UPCOMING_MEETINGS_ENABLED: boolean = true

/**
 * Whether the Project Management navigation and routes are available.
 *
 * On: the sidebar lists the section, and `/projects` + `/projects/:projectKey`
 * render their pages instead of redirecting home. The redirect and the hidden
 * nav item both stay behind this flag, so holding the feature back again is
 * still the one-line change it was.
 *
 * Creating a project is a separate decision with its own blocker and stays
 * off — see `PROJECT_CREATE_ENABLED`.
 */
export const PROJECT_MANAGEMENT_ENABLED: boolean = true

/**
 * Whether calendar rows expose the AI-generated meeting Brief action.
 *
 * On everywhere, production builds included. It read `import.meta.env.DEV`
 * while Brief was unreleased, which is why the action showed on a dev run and
 * not on a deployed one. Calendar events and Join actions never depended on
 * this flag; only the Brief entry point does.
 *
 * `Home/index.test.tsx` and `Upcoming/index.test.tsx` both parameterize it, so
 * either state stays covered.
 */
export const MEETING_BRIEF_ENABLED: boolean = true

/**
 * Whether the Google Calendar panel is offered alongside the Microsoft one.
 *
 * Off while the Google Cloud project's OAuth consent screen is still in Testing: in that state
 * Google only admits accounts on the project's test-user list and answers everybody else with
 * `403 access_denied`, so the panel would hand most people a Connect button that cannot work
 * (BRAI-542 was that error reaching users).
 *
 * Only the two panels are hidden. `services/google-calendar`, the `/api/calendar/google/*` routes,
 * the strings and `UpcomingCalendar`'s own `google` branch all stay, so turning this on is a
 * one-line change and anyone already connected keeps their connection.
 *
 * Google sign-in on the Login page is deliberately NOT covered by this. It is a separate decision
 * about a separate flow, and silently taking away a way to sign in is not the same kind of change
 * as hiding a panel.
 *
 * Annotated `boolean` rather than left to infer `false`, so neither side of the flag reads as dead
 * code while it is off.
 */
export const GOOGLE_CALENDAR_ENABLED: boolean = false

/**
 * Whether each finalized transcript segment shows its `mm:ss – mm:ss` span.
 *
 * Off by default: the panes normally mark time only when the speaker changes or
 * after a 30s run by one speaker, which keeps them readable. Turned on, every
 * finalized segment carries its own span in both panes, so Input and Output can
 * be read against each other line for line — which is how the ordering fixed in
 * BRAI-439 gets checked. That is a diagnostic view, not the reading experience.
 */
export const TRANSCRIPT_TIMINGS_ENABLED: boolean = false

/**
 * Whether the assist / Copilot panel (`assist.card` / `assist.ask`) is shown during a meeting.
 *
 * Copilot assist (`assist.ask` / `assist.card`) is handled by the **AI STT proxy**
 * (`VITE_AI_PROXY_URL`, e.g. `…/v1/ai/ws/stt`), not NestJS on port 3001. That service must
 * expose assist on the STT WebSocket: suggestion cards (`assist.card`) and the answer
 * flow for `assist.ask` (a `not_supported` / “no answer flow” error means the proxy
 * lacks the ask handler even when the UI flag is on). Nest on :3001 is unrelated.
 */
export const ASSIST_PANEL_UI_ENABLED: boolean = true

/**
 * Whether the Copilot panel renders its suggestion cards only.
 *
 * Off: the panel shows the suggestions for the current turn *and* the answer
 * cards that asking produces, plus the backlog-ticket and conflict-warning cards
 * the same socket sends. Turned on it would hide everything except the
 * suggestions, which leaves a question with nowhere to land once the user asks
 * one.
 *
 * `CopilotContent.test.tsx` covers both states, so flipping this back to `true`
 * does not arrive untested. Annotated `boolean` so neither side reads as dead
 * code while it is off.
 */
export const ASSIST_SUGGESTIONS_ONLY: boolean = false

/**
 * Whether the Copilot panel states the transcript turn a suggestion came from.
 *
 * On: every question is drawn inside the turn it was raised from — `Speaker ·
 * mm:ss – “…”` above the question, whether or not the AI has answered it yet.
 *
 * Off: the questions and their answers are drawn with no turn line at all.
 *
 * `CopilotContent.test.tsx` covers both states, so flipping this back to `true`
 * does not arrive untested. Annotated `boolean` so neither side reads as dead
 * code while it is off.
 */
export const ASSIST_SHOW_TURN_CONTEXT: boolean = true

/**
 * Whether the Project Management screen offers a "Create project" button.
 *
 * Off: there is nothing for it to call. Projects come from Backlog under the
 * reader's own grant, and the local `Project` table has exactly one writer —
 * the AI service's register import — which NestJS has never touched. Creating
 * one here would mean either a second writer for a shared table or a write
 * into somebody's Backlog space, and neither is decided yet.
 *
 * The screen, its strings and its layout all stay as they are, so turning this
 * on is a one-line change once a destination exists.
 *
 * Annotated `boolean` rather than left to infer `false`, so neither side of the
 * flag reads as dead code while it is off.
 */
export const PROJECT_CREATE_ENABLED: boolean = false

/**
 * Whether the "send a picture of the shared tab" checkbox can be ticked.
 *
 * Off: the control stays on screen, greyed, so the capability is still
 * visible and nobody is left wondering where it went — but it cannot be
 * turned on, and `sendScreenFrames` leaves the form `false` whatever the
 * state behind it says.
 *
 * Everything else stays: the state, the payload field, the hint and the
 * dependency on Share tab audio, so turning this back on is a one-line
 * change.
 *
 * Annotated `boolean` rather than left to infer `false`, so neither side of
 * the flag reads as dead code while it is off.
 */
export const SEND_SCREEN_FRAMES_ENABLED: boolean = false
